import { useEffect, useState } from "react";
import { ref, getDownloadURL } from "firebase/storage";
import { storage } from "../lib/firebase";

type Props = {
  storagePath: string | null;
  imageMissing?: boolean;
  alt?: string;
};

/**
 * Renderiza un thumbnail clickeable de una boleta guardada en Firebase Storage.
 * Click abre modal fullscreen con la imagen.
 * Si imageMissing=true muestra placeholder.
 */
export function BoletaImageViewer({ storagePath, imageMissing, alt }: Props) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!storagePath) return;
    getDownloadURL(ref(storage, storagePath))
      .then((u) => { if (alive) setUrl(u); })
      .catch((e) => { if (alive) setError(e.message); });
    return () => { alive = false; };
  }, [storagePath]);

  if (imageMissing) {
    return (
      <div className="grid h-16 w-16 shrink-0 place-items-center rounded-lg bg-amber-50 text-[10px] text-amber-700 ring-1 ring-amber-200">
        Sin foto
      </div>
    );
  }

  if (!storagePath) {
    return (
      <div className="grid h-16 w-16 shrink-0 place-items-center rounded-lg bg-leaf-50 text-[10px] text-leaf-500 ring-1 ring-leaf-100">
        —
      </div>
    );
  }

  if (error) {
    return (
      <div className="grid h-16 w-16 shrink-0 place-items-center rounded-lg bg-red-50 text-[10px] text-red-700 ring-1 ring-red-200" title={error}>
        Error
      </div>
    );
  }

  if (!url) {
    return <div className="h-16 w-16 shrink-0 animate-pulse rounded-lg bg-leaf-100" />;
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="h-16 w-16 shrink-0 overflow-hidden rounded-lg ring-1 ring-leaf-200 hover:ring-leaf-500"
        aria-label="Ver boleta"
      >
        <img src={url} alt={alt ?? "Boleta"} className="h-full w-full object-cover" />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-black/80 p-4"
          onClick={() => setOpen(false)}
        >
          <div className="relative max-h-[90vh] max-w-md" onClick={(e) => e.stopPropagation()}>
            <img src={url} alt={alt ?? "Boleta"} className="max-h-[90vh] w-auto rounded-2xl object-contain" />
            <button
              onClick={() => setOpen(false)}
              className="absolute right-2 top-2 grid h-8 w-8 place-items-center rounded-full bg-white text-leaf-900 shadow-lg"
              aria-label="Cerrar"
            >
              ✕
            </button>
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-white px-3 py-1.5 text-xs font-medium text-leaf-800 shadow-lg"
            >
              Abrir en pestaña nueva
            </a>
          </div>
        </div>
      )}
    </>
  );
}
